import { useState, useMemo } from 'react'
import { v4 as uuid } from 'uuid'
import {
  Sparkles, Plus, X, Trash2, Phone, MapPin, Calendar, Sun, Moon, FileText,
  Building2, BedDouble, Sofa, Briefcase, ShowerHead, ChefHat, MessageCircle, Pencil,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  getOportunidadesArco, saveOportunidadeArco, deleteOportunidadeArco,
} from '../utils/storage'
import { formatTelefone } from '../utils/fmt'
import { useAuth } from '../contexts/AuthContext'

// ── Catálogos ────────────────────────────────────────────────────────────────

const AMBIENTES = [
  { key: 'Quarto',      icon: BedDouble  },
  { key: 'Sala',        icon: Sofa       },
  { key: 'WC',          icon: ShowerHead },
  { key: 'Banheiro',    icon: ShowerHead },
  { key: 'Cozinha',     icon: ChefHat    },
  { key: 'Empresa',     icon: Building2  },
  { key: 'Escritório',  icon: Briefcase  },
  { key: 'Closet',      icon: BedDouble  },
]

const DIAS_SEMANA = ['Segunda','Terça','Quarta','Quinta','Sexta','Sábado']
const TURNOS = ['Manhã','Tarde']

const STATUS_OPTIONS = [
  { value: 'Aberto',     bg: '#FFFDE7', text: '#CC8800', border: '#F5C200' },
  { value: 'Em contato', bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
  { value: 'Enviado',    bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
  { value: 'Fechado',    bg: '#FCE7F3', text: '#9D174D', border: '#F9A8D4' },
  { value: 'Perdido',    bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' },
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function whatsappLink(telefone) {
  const num = String(telefone || '').replace(/\D/g, '')
  if (!num) return null
  return `https://wa.me/55${num.startsWith('55') ? num.slice(2) : num}`
}

function formatCriadoEm(iso) {
  try { return format(parseISO(iso), "dd/MM/yy 'às' HH:mm", { locale: ptBR }) } catch { return '' }
}

// ── Modal de cadastro/edição ─────────────────────────────────────────────────

const FORM_DEFAULT = {
  id:          '',
  nome:        '',
  telefone:    '',
  bairro:      '',
  cidade:      '',
  ambientes:   [],
  temProjeto:  '',
  diasSemana:  [],
  turno:       '',
  observacoes: '',
  status:      'Aberto',
}

function FormModal({ open, onClose, oportunidade, onSave, vendedora }) {
  const [form, setForm] = useState(() => oportunidade ? { ...FORM_DEFAULT, ...oportunidade } : { ...FORM_DEFAULT })

  if (!open) return null

  function toggleArray(campo, valor) {
    setForm(f => {
      const arr = f[campo] || []
      return { ...f, [campo]: arr.includes(valor) ? arr.filter(x => x !== valor) : [...arr, valor] }
    })
  }

  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })) }

  function salvar() {
    if (!form.nome.trim() || !form.telefone.trim()) {
      alert('Nome e telefone são obrigatórios')
      return
    }
    const agora = new Date().toISOString()
    const final = {
      ...form,
      id:        form.id || uuid(),
      criadoEm:  form.criadoEm || agora,
      atualizadoEm: agora,
      vendedora: form.vendedora || vendedora || '',
    }
    onSave(final)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#111111] text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#F5C200]" />
            <h2 className="font-bold text-base">
              {oportunidade ? 'Editar oportunidade' : 'Nova oportunidade Arco'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">

          {/* Nome + telefone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Nome *</label>
              <input
                type="text" value={form.nome} onChange={e => set('nome', e.target.value)}
                className="input text-sm" placeholder="Nome do lead"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Telefone *</label>
              <input
                type="tel" maxLength={15} value={form.telefone}
                onChange={e => set('telefone', formatTelefone(e.target.value))}
                className="input text-sm" placeholder="(83) 9..."
              />
            </div>
          </div>

          {/* Bairro + cidade */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Bairro</label>
              <input
                type="text" value={form.bairro} onChange={e => set('bairro', e.target.value)}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Cidade</label>
              <input
                type="text" value={form.cidade} onChange={e => set('cidade', e.target.value)}
                className="input text-sm"
              />
            </div>
          </div>

          {/* Ambientes (checklist) */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">
              Ambientes desejados
            </label>
            <div className="flex flex-wrap gap-2">
              {AMBIENTES.map(({ key, icon: Icon }) => {
                const on = form.ambientes.includes(key)
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleArray('ambientes', key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm transition-all
                      ${on
                        ? 'bg-[#F5C200]/15 border-[#F5C200] text-[#111111] font-semibold'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                  >
                    <Icon size={14} />
                    {key}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Projeto arquiteto */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">
              Já tem projeto de arquiteto?
            </label>
            <div className="flex gap-2">
              {['Sim','Não'].map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => set('temProjeto', opt)}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all
                    ${form.temProjeto === opt
                      ? 'bg-[#111111] border-[#111111] text-[#F5C200]'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Dias da semana */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">
              Melhor(es) dia(s) para visita
            </label>
            <div className="flex flex-wrap gap-2">
              {DIAS_SEMANA.map(dia => {
                const on = form.diasSemana.includes(dia)
                return (
                  <button
                    key={dia}
                    type="button"
                    onClick={() => toggleArray('diasSemana', dia)}
                    className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all
                      ${on
                        ? 'bg-[#F5C200]/15 border-[#F5C200] text-[#111111]'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                  >
                    {dia}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Turno */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">Turno</label>
            <div className="flex gap-2">
              {TURNOS.map(t => {
                const Icon = t === 'Manhã' ? Sun : Moon
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set('turno', t)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all
                      ${form.turno === t
                        ? 'bg-[#111111] border-[#111111] text-[#F5C200]'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                  >
                    <Icon size={14} />
                    {t}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => {
                const on = form.status === s.value
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => set('status', s.value)}
                    style={on
                      ? { backgroundColor: s.bg, borderColor: s.border, color: s.text }
                      : {}
                    }
                    className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all
                      ${on ? '' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'}`}
                  >
                    {s.value}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-1">Observações</label>
            <textarea
              value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
              className="input text-sm min-h-[80px]"
              placeholder="Detalhes adicionais sobre o lead, expectativa, urgência, orçamento estimado…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={salvar} className="btn-primary">Salvar</button>
        </div>
      </div>
    </div>
  )
}

// ── Linha de oportunidade (formato tabela/planilha) ──────────────────────────
// Vista compacta em linhas — cabe muito mais lead na tela que os cards,
// e o botão WhatsApp verde chama atenção pra ação principal (contatar o
// lead). Observações e ambientes viram tooltip/segunda linha só quando existem.

function LinhaOportunidade({ o, onEdit, onDelete }) {
  const sStyle = STATUS_OPTIONS.find(s => s.value === o.status) || STATUS_OPTIONS[0]
  const wa     = whatsappLink(o.telefone)
  const local  = [o.bairro, o.cidade].filter(Boolean).join(' · ')

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors align-top">
      {/* Cliente */}
      <td className="py-2.5 px-3">
        <div className="font-semibold text-slate-800 text-sm">{o.nome}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">
          {o.vendedora ? `por ${o.vendedora} · ` : ''}{formatCriadoEm(o.criadoEm)}
        </div>
      </td>

      {/* Telefone */}
      <td className="py-2.5 px-3 whitespace-nowrap">
        {o.telefone
          ? <span className="text-sm text-slate-700">{formatTelefone(o.telefone)}</span>
          : <span className="text-xs text-slate-300 italic">—</span>
        }
      </td>

      {/* Local */}
      <td className="py-2.5 px-3">
        {local
          ? <div className="flex items-center gap-1 text-sm text-slate-600"><MapPin size={12} className="text-slate-400 shrink-0" />{local}</div>
          : <span className="text-xs text-slate-300 italic">—</span>
        }
      </td>

      {/* Status */}
      <td className="py-2.5 px-3">
        <span
          style={{ backgroundColor: sStyle.bg, color: sStyle.text, borderColor: sStyle.border }}
          className="text-[10px] font-bold px-2 py-1 rounded-full border whitespace-nowrap inline-block"
        >
          {o.status}
        </span>
      </td>

      {/* Ambientes */}
      <td className="py-2.5 px-3">
        {o.ambientes?.length > 0
          ? (
            <div className="flex flex-wrap gap-1 max-w-[200px]">
              {o.ambientes.map(a => (
                <span key={a} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{a}</span>
              ))}
            </div>
          )
          : <span className="text-xs text-slate-300 italic">—</span>
        }
      </td>

      {/* Detalhes: projeto/dia/turno/obs — compactos em uma célula só */}
      <td className="py-2.5 px-3">
        <div className="flex flex-col gap-0.5 text-[11px] text-slate-500">
          {o.temProjeto && (
            <span className="flex items-center gap-1"><FileText size={10} />Proj: <strong className="text-slate-700">{o.temProjeto}</strong></span>
          )}
          {o.diasSemana?.length > 0 && (
            <span className="flex items-center gap-1"><Calendar size={10} /><strong className="text-slate-700">{o.diasSemana.join(', ')}</strong></span>
          )}
          {o.turno && (
            <span className="flex items-center gap-1">
              {o.turno === 'Manhã' ? <Sun size={10} /> : <Moon size={10} />}
              <strong className="text-slate-700">{o.turno}</strong>
            </span>
          )}
          {o.observacoes && (
            <span className="italic text-slate-400 truncate max-w-[220px]" title={o.observacoes}>{o.observacoes}</span>
          )}
        </div>
      </td>

      {/* Ações — WhatsApp em destaque, depois editar/excluir */}
      <td className="py-2.5 px-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5 justify-end">
          {wa ? (
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              title={`Falar no WhatsApp com ${o.nome}`}
              className="inline-flex items-center gap-1 bg-[#25D366] hover:bg-[#1FBA57] text-white text-xs font-semibold px-2.5 py-1.5 rounded transition-colors"
            >
              <MessageCircle size={13} />
              WhatsApp
            </a>
          ) : (
            <span className="text-[10px] text-slate-300 italic px-2">sem telefone</span>
          )}
          <button
            onClick={() => onEdit(o)}
            title="Editar"
            className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => onDelete(o.id)}
            title="Excluir"
            className="p-1.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  )
}

// ── Página ───────────────────────────────────────────────────────────────────

export function OportunidadesArco() {
  const { profile }                       = useAuth()
  const vendedora                         = profile?.nome || profile?.username || ''
  const [oportunidades, setOportunidades] = useState(() => getOportunidadesArco())
  const [modalOpen, setModalOpen]         = useState(false)
  const [editando, setEditando]           = useState(null)
  const [filtroStatus, setFiltroStatus]   = useState('TODOS')
  const [busca, setBusca]                 = useState('')

  function abrirNova() { setEditando(null); setModalOpen(true) }
  function abrirEditar(o) { setEditando(o); setModalOpen(true) }

  function handleSave(o) {
    saveOportunidadeArco(o)
    setOportunidades(getOportunidadesArco())
  }

  function handleDelete(id) {
    if (!confirm('Excluir esta oportunidade?')) return
    deleteOportunidadeArco(id)
    setOportunidades(getOportunidadesArco())
  }

  const filtradas = useMemo(() => {
    let l = oportunidades
    if (filtroStatus !== 'TODOS') l = l.filter(o => o.status === filtroStatus)
    if (busca.trim()) {
      const t = busca.toLowerCase().trim()
      l = l.filter(o =>
        (o.nome     || '').toLowerCase().includes(t) ||
        (o.telefone || '').toLowerCase().includes(t) ||
        (o.bairro   || '').toLowerCase().includes(t) ||
        (o.cidade   || '').toLowerCase().includes(t)
      )
    }
    return l
  }, [oportunidades, filtroStatus, busca])

  const contagemPorStatus = useMemo(() => {
    const map = { TODOS: oportunidades.length }
    STATUS_OPTIONS.forEach(s => { map[s.value] = oportunidades.filter(o => o.status === s.value).length })
    return map
  }, [oportunidades])

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles size={20} className="text-[#F5C200]" />
            Oportunidades Arco
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Leads qualificados de móveis planejados pra Arco Marcenaria
          </p>
        </div>
        <button onClick={abrirNova} className="btn-primary">
          <Plus size={15} />
          Nova oportunidade
        </button>
      </div>

      {/* Filtros */}
      <div className="card p-3 mb-5 flex flex-wrap items-center gap-2">
        <input
          type="text" value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar por nome, telefone, bairro…"
          className="input text-sm flex-1 min-w-[200px]"
        />
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setFiltroStatus('TODOS')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
              ${filtroStatus === 'TODOS' ? 'bg-[#111111] text-[#F5C200]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          >
            Todos ({contagemPorStatus.TODOS})
          </button>
          {STATUS_OPTIONS.map(s => (
            <button
              key={s.value}
              onClick={() => setFiltroStatus(s.value)}
              style={filtroStatus === s.value
                ? { backgroundColor: s.bg, color: s.text, borderColor: s.border }
                : {}
              }
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
                ${filtroStatus === s.value ? '' : 'bg-slate-100 border-transparent text-slate-500 hover:bg-slate-200'}`}
            >
              {s.value} ({contagemPorStatus[s.value] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {filtradas.length === 0 ? (
        <div className="card p-10 text-center">
          <Sparkles className="mx-auto text-slate-200 mb-3" size={32} />
          <p className="text-sm text-slate-400">
            {oportunidades.length === 0
              ? 'Nenhuma oportunidade cadastrada ainda'
              : 'Nenhuma oportunidade corresponde aos filtros'
            }
          </p>
          {oportunidades.length === 0 && (
            <button onClick={abrirNova} className="btn-primary mx-auto mt-4 text-sm">
              <Plus size={15} /> Cadastrar a primeira
            </button>
          )}
        </div>
      ) : (
        // Tabela em linhas (vista planilha) — cabe mais lead na tela.
        // overflow-x-auto no wrapper pra celular não estourar layout.
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 px-3 font-semibold">Cliente</th>
                <th className="text-left py-2 px-3 font-semibold">Telefone</th>
                <th className="text-left py-2 px-3 font-semibold">Local</th>
                <th className="text-left py-2 px-3 font-semibold">Status</th>
                <th className="text-left py-2 px-3 font-semibold">Ambientes</th>
                <th className="text-left py-2 px-3 font-semibold">Detalhes</th>
                <th className="text-right py-2 px-3 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(o => (
                <LinhaOportunidade key={o.id} o={o} onEdit={abrirEditar} onDelete={handleDelete} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        oportunidade={editando}
        onSave={handleSave}
        vendedora={vendedora}
      />
    </div>
  )
}
